import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zhTW } from "date-fns/locale";
import { useNavigate, useParams } from "react-router-dom";
import { MdError, MdKeyboardArrowLeft, MdOutlineUploadFile } from "react-icons/md";
import { FiPlus, FiTrash2 } from "react-icons/fi";

import Button from "../Utilities/Button";
import Datepicker from "../Utilities/Datepicker/Datepicker";
import TextField from "../Utilities/TextField";
import Switcher from "../Utilities/AlertSwitch/Switcher";
import Toast from "../Utilities/Toast";
import Alert from "../Utilities/Alert";
import RightPanelLayout from "../Layout/RightPanelLayout";

import EventRichTextEditor from "../EventManagement/EventRichTextEditor";
import SplitHourMinuteTimeInput from "../EventManagement/SplitHourMinuteTimeInput";
import EventStatusBadge from "../EventManagement/EventStatusBadge";

import baseStyles from "../EventManagement/style/EventFormPage.module.scss";
import releaseStyles from "./style/ReleaseNoteFormPage.module.scss";
import ReleaseNotePreviewModal from "./ReleaseNotePreviewModal";
import {
  buildReleaseNotePayload,
  extractReleaseNoteImageDisplayItems,
  normalizeTagsFromApi,
  toLocalDatetimeInput,
} from "./releaseNotesTransforms";
import {
  createReleaseNote,
  createReleaseNoteMultipart,
  deleteReleaseNote,
  fetchReleaseNoteById,
  updateReleaseNoteMultipart,
  updateReleaseNote,
} from "./releaseNotesApi";

const nowLocalForInput = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

const isValidRedirectUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return true;
  try {
    const parsed = new URL(raw);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const ReleaseNoteFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const initialForm = useMemo(
    () => ({
      title: "",
      subtitle: "",
      content: "",
      isPublished: false,
      publishedAt: "",
      tags: [],
      redirectButtonText: "",
      redirectUrl: "",
    }),
    [],
  );

  const {
    watch,
    setValue,
    reset,
    getValues,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: initialForm,
  });

  const form = watch();

  const [detailLoading, setDetailLoading] = useState(mode === "edit");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toolbarSaveWarning, setToolbarSaveWarning] = useState("");
  const [isConfirmLeaveOpen, setIsConfirmLeaveOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [tagDraftName, setTagDraftName] = useState("");
  const [toastState, setToastState] = useState({
    show: false,
    title: "",
    subtitle: "",
    error: false,
  });
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const imageInputRef = useRef(null);

  const showToast = (title, error = false) => {
    setToastState({ show: true, title, subtitle: "", error });
    window.setTimeout(() => {
      setToastState((s) => ({ ...s, show: false }));
    }, 3200);
  };

  useEffect(() => {
    if (mode !== "edit" || !id) {
      setDetailLoading(false);
      reset(initialForm);
      clearErrors();
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    fetchReleaseNoteById(id)
      .then((data) => {
        if (cancelled) return;
        reset({
          title: data?.title ?? "",
          subtitle: typeof data?.subtitle === "string" ? data.subtitle : "",
          content: data?.content ?? "",
          isPublished: Boolean(data?.is_published),
          publishedAt: toLocalDatetimeInput(data?.published_at ?? ""),
          tags: normalizeTagsFromApi(data?.meta_json?.tags ?? data?.tags),
          redirectButtonText:
            data?.url_context ??
            data?.cta_label ??
            data?.cta_button_text ??
            data?.button_text ??
            data?.meta_json?.cta_label ??
            "",
          redirectUrl:
            data?.url_path ??
            data?.cta_url ??
            data?.link_url ??
            data?.redirect_url ??
            data?.meta_json?.cta_url ??
            "",
        });
        setExistingImages(extractReleaseNoteImageDisplayItems(data));
        clearErrors();
      })
      .catch(() => {
        if (cancelled) return;
        navigate("/dashboard/tdp/release-notes");
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, id, reset, clearErrors, navigate, initialForm]);

  useEffect(() => {
    if (mode === "new") {
      reset(initialForm);
      clearErrors();
      setToolbarSaveWarning("");
      setTagDraftName("");
      setImageFiles([]);
      setExistingImages([]);
    }
  }, [mode, initialForm, reset, clearErrors]);

  useEffect(() => {
    if (!isDirty) return undefined;

    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setIsConfirmLeaveOpen(true);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isDirty]);

  const publishedDatePart =
    form.publishedAt && form.publishedAt.length >= 10
      ? form.publishedAt.slice(0, 10)
      : "";

  const publishedTimePart =
    form.publishedAt && form.publishedAt.length >= 16
      ? form.publishedAt.slice(11, 16)
      : "";

  const publishedDateForPicker = useMemo(() => {
    if (!publishedDatePart) return "";
    const d = new Date(`${publishedDatePart}T12:00:00`);
    return Number.isNaN(d.getTime()) ? "" : d;
  }, [publishedDatePart]);

  const handlePublishedDatePick = (date) => {
    const prev = getValues();
    if (!date) {
      setValue("publishedAt", "", { shouldDirty: true });
      return;
    }

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const t =
      prev.publishedAt && prev.publishedAt.length >= 16
        ? prev.publishedAt.slice(11, 16)
        : "10:00";
    setValue("publishedAt", `${yyyy}-${mm}-${dd}T${t}`, { shouldDirty: true });
  };

  const onPublishedTimeCommit = (hhmm) => {
    const t = hhmm || "00:00";
    const prev = getValues();
    const date =
      prev.publishedAt && prev.publishedAt.length >= 10
        ? prev.publishedAt.slice(0, 10)
        : nowLocalForInput().slice(0, 10);
    setValue("publishedAt", `${date}T${t}`, { shouldDirty: true });
  };

  const setField = (key, value) => {
    setValue(key, value, { shouldDirty: true });
    if (key === "redirectUrl" || (value !== "" && value != null)) {
      clearErrors(key);
    }
    if (key === "title" && toolbarSaveWarning) {
      setToolbarSaveWarning("");
    }
  };

  const handleBack = () => {
    if (!isDirty) {
      navigate("/dashboard/tdp/release-notes");
      return;
    }
    setIsConfirmLeaveOpen(true);
  };

  const handleLeaveConfirm = () => {
    setIsConfirmLeaveOpen(false);
    navigate("/dashboard/tdp/release-notes");
  };

  const handleSave = async () => {
    if (isSubmitting) return;
    setToolbarSaveWarning("");
    clearErrors();

    const title = String(form.title || "").trim();
    if (!title) {
      setError("title", { type: "manual", message: "此欄位為必填" });
      setToolbarSaveWarning("未輸入標題");
      return;
    }

    const redirectUrl = String(form.redirectUrl || "").trim();
    if (!isValidRedirectUrl(redirectUrl)) {
      setError("redirectUrl", {
        type: "manual",
        message: "請輸入有效網址（http:// 或 https://）",
      });
      setToolbarSaveWarning("導連網址格式不正確");
      return;
    }

    const safePublishedAt = form.isPublished ? form.publishedAt || nowLocalForInput() : "";
    const tagNames = (form.tags || []).map((t) => t?.name).filter(Boolean);

    const payload = buildReleaseNotePayload({
      title: form.title,
      content: form.content,
      isPublished: form.isPublished,
      publishedAtLocal: safePublishedAt,
      tags: tagNames,
      redirectButtonText: form.redirectButtonText,
      redirectUrl: form.redirectUrl,
    });

    if (!payload) {
      showToast("儲存失敗請重試", true);
      return;
    }

    setIsSubmitting(true);
    try {
      if (imageFiles.length > 0) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value != null) {
            if (key === "meta_json") {
              const tags = Array.isArray(value?.tags) ? value.tags : [];
              if (tags.length > 0) {
                tags.forEach((tag) => {
                  const name = String(tag || "").trim();
                  if (name) {
                    formData.append("meta_json[tags][]", name);
                  }
                });
              } else {
                formData.append("meta_json[tags]", "");
              }
            } else {
              formData.append(key, String(value));
            }
          }
        });

        imageFiles.forEach((file) => {
          if (file) formData.append("images[]", file);
        });

        if (mode === "new") {
          await createReleaseNoteMultipart(formData);
        } else {
          await updateReleaseNoteMultipart(id, formData);
        }
      } else if (mode === "new") {
        await createReleaseNote(payload);
      } else {
        await updateReleaseNote(id, payload);
      }

      showToast(
        form.isPublished ? `已上架發行日記「${title}」。` : `已儲存發行日記草稿「${title}」。`,
        false,
      );
      navigate("/dashboard/tdp/release-notes");
    } catch {
      showToast("儲存失敗請重試", true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleteOpen(false);
    try {
      await deleteReleaseNote(id);
      showToast("已刪除發行日記", false);
      navigate("/dashboard/tdp/release-notes");
    } catch {
      showToast("刪除失敗請重試", true);
    }
  };

  const tagList = form.tags || [];

  const previewImageUrls = useMemo(
    () => imageFiles.map((file) => URL.createObjectURL(file)),
    [imageFiles],
  );

  const mergedPreviewImageUrls = useMemo(() => {
    const existing = Array.isArray(existingImages)
      ? existingImages.map((x) => x.url).filter(Boolean)
      : [];
    return [...existing, ...previewImageUrls];
  }, [existingImages, previewImageUrls]);

  useEffect(() => {
    return () => {
      previewImageUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewImageUrls]);

  const handlePreview = () => {
    setIsPreviewOpen(true);
  };

  const appendTag = () => {
    const name = String(tagDraftName || "").trim();
    if (!name) return;

    const prev = getValues("tags") || [];
    const next = [
      ...prev,
      {
        id: `tag-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        name,
      },
    ];
    setValue("tags", next, { shouldDirty: true });
    setTagDraftName("");
  };

  const removeTag = (tagId) => {
    const prev = getValues("tags") || [];
    setValue(
      "tags",
      prev.filter((t) => t.id !== tagId),
      { shouldDirty: true },
    );
  };

  const onSelectImages = (e) => {
    const files = Array.from(e.target.files || []).filter((f) =>
      String(f.type || "").startsWith("image/"),
    );
    e.target.value = "";
    setImageFiles(files);
  };

  if (detailLoading) {
    return (
      <RightPanelLayout title='發行日記'>
        <div className={releaseStyles.previewSaveHint}>載入中…</div>
      </RightPanelLayout>
    );
  }

  const statusText = form.isPublished ? "上架" : "未上架";
  const statusBadgeTheme = form.isPublished ? "published" : "unpublished";
  const statusBadgeText = form.isPublished ? "已上架" : "未上架";

  return (
    <RightPanelLayout title='發行日記'>
      <form
        className={baseStyles.form}
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <div className={baseStyles.topToolbarHeader}>
          <button
            type='button'
            className={baseStyles.backButton}
            onClick={handleBack}
          >
            <MdKeyboardArrowLeft size={32} />
            返回
          </button>

          <div className={baseStyles.rightActions}>
            {toolbarSaveWarning ? (
              <span className={baseStyles.toolbarWarningText}>
                <MdError size={22} aria-hidden />
                {toolbarSaveWarning}
              </span>
            ) : null}

            <EventStatusBadge
              theme={statusBadgeTheme}
              text={statusBadgeText}
            />

            <button
              type='button'
              className={baseStyles.previewButton}
              onClick={handlePreview}
            >
              預覽
            </button>

            <Button
              type='primary'
              title={isSubmitting ? "儲存中…" : "儲存"}
              disabled={isSubmitting}
            />

            {mode === "edit" ? (
              <button
                type='button'
                className={baseStyles.deleteButton}
                onClick={() => setIsDeleteOpen(true)}
                aria-label='刪除發行日記'
              >
                <FiTrash2 size={24} aria-hidden />
                刪除
              </button>
            ) : null}
          </div>
        </div>

        <div className={releaseStyles.whiteCard}>
          <div className={baseStyles.columnStack}>
            <div className={baseStyles.infoRow}>
              <div className={baseStyles.infoRowLabel}>
                標題<span className={baseStyles.requiredPrimary}>*</span>
              </div>
              <div className={baseStyles.infoRowBody}>
                <TextField
                  placeholder='請輸入'
                  width='100%'
                  fullWidth
                  value={form.title}
                  error={Boolean(errors.title)}
                  onChange={(v) => setField("title", v)}
                />
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div className={baseStyles.infoRowLabel}>發佈時間</div>
              <div className={baseStyles.infoRowBody}>
                <div className={baseStyles.activityTimeControls}>
                  <div className={baseStyles.activityDatePickerWrap}>
                    <Datepicker
                      selectDate={publishedDateForPicker}
                      setSelectDate={handlePublishedDatePick}
                      error={Boolean(errors.publishedAt)}
                      iconType='arrow'
                      placeholderText='請選擇日期'
                      dateFormat='yyyy-MM-dd'
                      locale={zhTW}
                    />
                  </div>
                  <SplitHourMinuteTimeInput
                    value={publishedTimePart || "10:00"}
                    onCommit={onPublishedTimeCommit}
                    ariaLabel='發佈時間'
                  />
                </div>
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div className={baseStyles.infoRowLabel}>狀態</div>
              <div className={baseStyles.infoRowBody}>
                <div className={baseStyles.switchRow}>
                  <Switcher
                    isChecked={form.isPublished}
                    onChange={() => {
                      const next = !form.isPublished;
                      setValue("isPublished", next, { shouldDirty: true });
                      if (next) {
                        const prevPublishedAt = getValues("publishedAt");
                        if (!prevPublishedAt) {
                          setValue("publishedAt", nowLocalForInput(), {
                            shouldDirty: true,
                          });
                        }
                      }
                    }}
                    color={{ on: "#5856D6", off: "#5856D64D" }}
                    width={33}
                    height={20}
                  />
                  <span className={releaseStyles.statusText}>{statusText}</span>
                </div>
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div className={baseStyles.infoRowLabel}>標籤</div>
              <div className={baseStyles.infoRowBody}>
                <div className={releaseStyles.tagInlineRow}>
                  <div className={releaseStyles.tagInputWrap}>
                    <TextField
                      placeholder='請輸入標籤名稱'
                      width='120px'
                      value={tagDraftName}
                      onChange={setTagDraftName}
                    />
                  </div>
                  <button
                    type='button'
                    className={baseStyles.tagAddButton}
                    onClick={appendTag}
                  >
                    <FiPlus size={18} aria-hidden />
                    新增
                  </button>
                </div>

                {tagList.length > 0 ? (
                  <div className={baseStyles.tagList}>
                    {tagList.map((tag) => (
                      <span
                        key={tag.id}
                        className={`${baseStyles.tagChip} ${releaseStyles.tagChipGrey}`}
                      >
                        {tag.name}
                        <button
                          type='button'
                          className={baseStyles.tagChipRemove}
                          onClick={() => removeTag(tag.id)}
                          aria-label={`移除標籤 ${tag.name}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div className={baseStyles.infoRowLabel}>圖片</div>
              <div className={baseStyles.infoRowBody}>
                <div className={releaseStyles.imageUploadLayout}>
                  <input
                    ref={imageInputRef}
                    type='file'
                    accept='image/*'
                    multiple
                    className={releaseStyles.hiddenInput}
                    onChange={onSelectImages}
                  />
                  <button
                    type='button'
                    className={releaseStyles.uploadButton}
                    onClick={() => imageInputRef.current?.click()}
                  >
                    <MdOutlineUploadFile size={18} aria-hidden />
                    上傳
                  </button>
                  <div className={releaseStyles.uploadedFileNameArea}>
                    {imageFiles.length > 0 ? (
                      <div className={releaseStyles.uploadedFileNames}>
                        {imageFiles.map((file) => (
                          <span
                            key={`${file.name}-${file.size}`}
                            className={releaseStyles.uploadedFileName}
                          >
                            {file.name}
                          </span>
                        ))}
                      </div>
                    ) : existingImages.length > 0 ? (
                      <div className={releaseStyles.uploadedFileNames}>
                        {existingImages.map((img, idx) => (
                          <span
                            key={`${img.url}-${idx}`}
                            className={releaseStyles.uploadedFileName}
                          >
                            {img.fileName || img.url}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`${baseStyles.infoRow} ${baseStyles.infoRowAlignStart}`}
            >
              <div className={baseStyles.infoRowLabel}>內文介紹</div>
              <div className={baseStyles.infoRowBody}>
                <div
                  className={
                    errors.content ? baseStyles.richTextErrorWrap : undefined
                  }
                >
                  <EventRichTextEditor
                    value={form.content}
                    onChange={(html) => setField("content", html)}
                  />
                </div>
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div className={baseStyles.infoRowLabel}>導連按鈕</div>
              <div className={baseStyles.infoRowBody}>
                <div className={releaseStyles.redirectFieldStack}>
                  <div className={releaseStyles.redirectButtonTextField}>
                    <TextField
                      placeholder='請輸入按鈕文字'
                      width='100%'
                      fullWidth
                      value={form.redirectButtonText}
                      onChange={(v) => setField("redirectButtonText", v)}
                    />
                  </div>
                  <TextField
                    placeholder='https://'
                    width='100%'
                    fullWidth
                    value={form.redirectUrl}
                    error={Boolean(errors.redirectUrl)}
                    onChange={(v) => setField("redirectUrl", v)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      <Alert
        isOpen={isConfirmLeaveOpen}
        onClose={() => setIsConfirmLeaveOpen(false)}
        onConfirm={handleLeaveConfirm}
        leftButtonText='留在此頁'
        rightButtonText='離開'
        mainTitle='尚未儲存，確定離開？'
        subTitle='離開後本次填寫的內容將不會保留。'
        isWarning
      />

      <ReleaseNotePreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        title={form.title}
        subtitle={form.subtitle}
        publishedAtRaw={form.publishedAt}
        contentHtml={form.content}
        tags={tagList}
        imageUrls={mergedPreviewImageUrls}
        redirectButtonText={form.redirectButtonText}
        redirectUrl={form.redirectUrl}
      />

      <Alert
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        leftButtonText='取消'
        rightButtonText='刪除'
        mainTitle='確認刪除發行日記？'
        subTitle='刪除後無法復原。'
        isWarning
      />

      <Toast
        show={toastState.show}
        title={toastState.title}
        subtitle={toastState.subtitle}
        error={toastState.error}
      />
    </RightPanelLayout>
  );
};

export default ReleaseNoteFormPage;

